/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ComponentRef, Injectable } from '@angular/core';
import {
    type BaseRequestOptionsArgs, type Filter, type HttpResponse, type Polling, type Rest, EventService, HttpService,
    I18nService, LazyloadService, LocalStorageService, PollingService, ToastService, UtilService,
} from '@dm/core';
import { SWITCH } from 'app/config/switch';
import { type Subscription, Observable, Subject } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { TaskBatchComponent } from '../components/task-batch/task-batch.component';
import { TaskBatchStrService } from '../components/task-batch/task-batch-str.service';
import { TaskResultComponent } from '../components/task-result/task-result.component';
import { type Omtask } from '../types/mo';
import { AuthService } from './auth.service';

export interface Task {
    mo: string; // mo对象，和rest中的mo对应
    taskName: string; // 任务名名称
    objName: string; // 对象名称
    needTips: boolean;
}

export interface UserTask {
    [taskId: string]: Task;
}

export interface TaskOptionsArgs extends BaseRequestOptionsArgs {
    showResult?: boolean; // 是否弹出执行结果
    frontendDisplayResult?: boolean; // 是否前端展示执行结果
    customTip?: string;
    customTipType?: string;
}

/**
 * 任务创建成功的其他参数
 */
export interface CreateSuccessTaskOpts {
    oriShowResult?: boolean;
    oriFrontendDisplayResult?: boolean;
    customTip?: string;
    customTipType?: string;
}

export enum TaskStatus {
    /**
     * 没有此任务
     */
    UNDO = 0,
    /**
     * 此任务在执行中
     */
    DOING = 1,
    /**
     * 此任务执行完成
     */
    DONE = 2,
}

@Injectable({ providedIn: 'root' })
export class TaskService {
    // 任务名称配置
    taskNameText = this.taskBatchStrService.taskNameText;

    // 前台展示的侧滑框开启状态
    windowStatus = false;

    taskBatchComponent: ComponentRef<any>;

    // 任务执行完成可订阅对象
    private taskExcuted$ = new Subject<{ success: boolean; task: Task; data: Omtask }>();

    private taskExcuting$ = new Subject<{ task: Task; data: Omtask }>();

    // task超时时间默认600秒
    private timeout = 600 * 1000;

    private taskPolling$: Polling;
    /**
     * 首次从localStorage加载任务查询时不需要tips提示，避免在用户离开期间任务早就执行完成了却提示用户
     */
    private isFirstLoadTask = true;

    constructor(
        private httpService: HttpService,
        private toastService: ToastService,
        private i18nService: I18nService,
        private eventService: EventService,
        private utilService: UtilService,
        private localStorageService: LocalStorageService,
        private authService: AuthService,
        private pollingService: PollingService,
        private lazyLoadService: LazyloadService,
        private taskBatchStrService: TaskBatchStrService,
    ) { }

    init(): void {
        this.pollTask();
    }

    /**
     * 创建POST任务
     * @param taskName 任务名称，如：创建存储池
     * @param objName 对象名称，如：toragePool001
     * @param rest rest
     * @param body 消息体
     * @param options http选项
     */
    post(taskName: string, objName: string, rest: Rest, body: any, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const showResult = options ? options.showResult !== false : true;
        const frontendDisplayResult = options ? options.frontendDisplayResult !== false : true;
        const customTip = options && options.customTip ? options.customTip : '';
        const customTipType = options && options.customTipType ? options.customTipType : 'info';

        return this.httpService.post(rest, body, {
            doException: false,
            timeout: this.timeout,
            ...options,
        })
            .pipe(
                tap(({ data }) => {
                    const createOptions: CreateSuccessTaskOpts = {
                        oriShowResult: showResult,
                        oriFrontendDisplayResult: frontendDisplayResult,
                        customTip,
                        customTipType,
                    };
                    this.createSussess(rest, taskName, objName, data.taskId, createOptions);
                }, (res) => {
                    this.createFailed(taskName, objName, res, showResult);
                }),
            );
    }

    /**
     * 创建PUT任务
     * @param taskName 任务名称，如：扩容存储池
     * @param objName 对象名称，如：toragePool001
     * @param rest rest
     * @param body 消息体
     * @param options http选项
     */
    put(taskName: string, objName: string, rest: Rest, body: any, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const showResult = options ? options.showResult !== false : true;
        const frontendDisplayResult = options ? options.frontendDisplayResult !== false : true;

        return this.httpService.put(rest, body, {
            doException: false,
            timeout: this.timeout,
            ...options,
            doSuccess: false,
        })
            .pipe(
                tap(({ data }) => {
                    const createOptions: CreateSuccessTaskOpts = {
                        oriShowResult: showResult,
                        oriFrontendDisplayResult: frontendDisplayResult,
                    };
                    this.createSussess(rest, taskName, objName, data.taskId, createOptions);
                }, (res) => {
                    this.createFailed(taskName, objName, res, showResult);
                }),
            );
    }

    /**
     * 创建DELETE任务
     * @param taskName 任务名称，如：扩容存储池
     * @param objName 对象名称，如：toragePool001
     * @param rest rest
     * @param options http选项
     */
    delete(taskName: string, objName: string, rest: Rest, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const showResult = options ? options.showResult !== false : true;
        const frontendDisplayResult = options ? options.frontendDisplayResult !== false : true;

        return this.httpService.delete(rest, {
            doException: false,
            timeout: this.timeout,
            ...options,
            doSuccess: false,
        })
            .pipe(
                tap(({ data }) => {
                    const createOptions: CreateSuccessTaskOpts = {
                        oriShowResult: showResult,
                        oriFrontendDisplayResult: frontendDisplayResult,
                    };
                    this.createSussess(rest, taskName, objName, data.taskId, createOptions);
                }, (res) => {
                    this.createFailed(taskName, objName, res, showResult);
                }),
            );
    }

    /**
     * 订阅任务执行回调
     * @param mo 模块名称, rest.ts中配置的那个mo
     * @param successFn 成功回调
     * @param failedFn 失败回调
     */
    subscribe(mo?: string | string[], successFn?: (task: Task, data: Omtask) => void, failedFn?: (task: Task, data: Omtask) => void): Subscription {
        return this.taskExcuted$.subscribe(({ success, task, data }) => {
            const isTaskMo = !mo || mo === task.mo || (Array.isArray(mo) && mo.includes(task.mo));
            if (isTaskMo) {
                if (success && successFn) {
                    successFn(task, data);
                } else if (!success && failedFn) {
                    failedFn(task, data);
                }
            }
        });
    }

    /**
     * 检查任务状态
     * 如果是在执行（包括等待）中，则轮询查询任务执行进度，一直到任务执行完成
     */
    checkTaskStatus(taskNames: string[]): Observable<{ taskStatus: TaskStatus; taskName?: string }> {
        let queryProgressTimer = null;
        return new Observable<{ taskStatus: TaskStatus; taskName?: string }>((observer) => {
            const rest = this.httpService.makeUrl('task.batchGet', 'V2');
            const groupFilters: Filter[][] = [];
            for (const taskName of taskNames) {
                groupFilters.push(
                    [{ field: 'name', matchMode: '', value: taskName }, { field: 'taskStatus', matchMode: '', value: 'executing' }],
                    [{ field: 'name', matchMode: '', value: taskName }, { field: 'taskStatus', matchMode: '', value: 'wait' }],
                );
            }
            rest.setGropFilters(groupFilters);

            const queryProgress = (taskId: string): Subscription => {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const restById = this.httpService.makeUrl('task.get', 'V2').format(taskId);
                return this.httpService.get(restById).subscribe(({ data }: { data: Omtask }) => {
                        if (data.taskStatus === 'fail' || data.taskStatus === 'success') {
                            observer.next({ taskStatus: TaskStatus.DONE, taskName: data.displayName });
                            observer.complete();
                        } else {
                            queryProgressTimer = setTimeout(() => {
                                queryProgress(taskId);
                            }, 5000);
                        }
                    }, () => {
                        observer.error();
                    });
            };

            this.httpService.get(rest).subscribe(({ data }) => {
                if (data?.length) {
                    observer.next({ taskStatus: TaskStatus.DOING, taskName: data[0].displayName });
                    queryProgress(data[0].id);
                } else {
                    observer.next({ taskStatus: TaskStatus.UNDO });
                }
            }, () => {
                observer.error();
            });
        }).pipe(finalize(() => {
            if (queryProgressTimer !== null) {
                clearTimeout(queryProgressTimer);
            }
        }));
    }

    /**
     * 通过taskId检查任务状态
     * 如果是在执行（包括等待）中，则轮询查询任务执行进度，一直到任务执行完成
     */
    checkTaskStatusById(taskId: string): Observable<{
        taskStatus: 'fail' | 'success';
        taskName?: string;
    }> {
        let queryProgressTimer = null;
        return new Observable<{ taskStatus: 'fail' | 'success'; taskName?: string }>((observer) => {
            const queryProgress = (id: string): Subscription => {
                const rest = this.httpService.makeUrl('task.get', 'V2').format(id);
                return this.httpService.get(rest)
                    .subscribe(({ data }: { data: Omtask }) => {
                        if (data.taskStatus === 'fail' || data.taskStatus === 'success') {
                            observer.next({ taskStatus: data.taskStatus, taskName: data.displayName });
                            observer.complete();
                        } else {
                            queryProgressTimer = setTimeout(() => {
                                queryProgress(id);
                            }, 5000);
                        }
                    }, () => {
                        observer.error();
                    });
            };
            queryProgress(taskId);
        }).pipe(
            finalize(() => {
                if (queryProgressTimer !== null) {
                    clearTimeout(queryProgressTimer);
                }
            }),
        );
    }

    /**
     * 添加任务
     * @param rest rest
     * @param objName 对象名称
     * @param taskId 任务ID
     */
    private addTask(rest: Rest, taskName: string, objName: string, taskId: number | string): void {
        const tasks = this.getTasks();
        const mo = rest.namespace.split('.')[2];

        tasks[taskId] = { mo, taskName, objName, needTips: true };
        this.saveTasks(tasks);
        this.taskPolling$?.immediate();
    }

    /**
     * 保存任务到localStorage
     */
    private saveTasks(tasks: UserTask): void {
        const userName = this.authService.getUserName();
        const allTasks = this.localStorageService.get('tasks') || {};
        allTasks[userName] = tasks;
        this.localStorageService.set('tasks', allTasks);
    }

    /**
     * 从localStorage中获取任务
     */
    private getTasks(): UserTask {
        const userName = this.authService.getUserName();
        const allTasks = this.localStorageService.get('tasks') || {};
        const tasks: UserTask = allTasks[userName] || {};

        if (this.isFirstLoadTask) {
            for (const taskId of Object.keys(tasks)) {
                tasks[taskId].needTips = false;
            }
        }

        return tasks;
    }

    /**
     * 任务创建成功
     * @param rest rest
     * @param taskName 任务名称
     * @param objName 对象名称
     * @param taskId 任务ID
     */
    private createSussess(
        rest: Rest,
        taskName: string,
        objName: string,
        taskId: number | string,
        options: CreateSuccessTaskOpts,
    ): void {
        const { oriShowResult, oriFrontendDisplayResult, customTip, customTipType = 'info' } = options;

        // 提示和侧滑框同时为true，展示侧滑框
        let frontendDisplayResult = oriFrontendDisplayResult;
        let showResult = oriShowResult;
        showResult = frontendDisplayResult && showResult ? false : showResult;

        if (SWITCH.HIDE_BATCH_TASK_RESULT) {
            frontendDisplayResult = false;
        }

        if (showResult) {
            let msg = `
                <div class="word-break">${this.i18nService.get('common.createTask_success_tip')}</div>
                <div>
                    <span>${this.i18nService.get('common.name_column', true)}</span>
                    <span class="word-break">${this.taskNameText[taskName]}</span>
                </div>
            `;

            if (objName) {
                msg += `<div>
                            <span>${this.i18nService.get('monitor.task_target_label', true)}</span>
                            <span class="word-break text-wrap">${this.utilService.encodeHTML(objName)}</span>
                        </div>`;
            }

            // 提示用户不要在任务执行中操作任务中的对象
            msg += `<div class="word-break task-success-warning">
                ${this.i18nService.get('common.createTask_success_warning_tip')}
            </div>`;
            this.toastService.success(msg);
        }

        // 前台展示omtask执行结果
        if (frontendDisplayResult) {
            this.windowStatus = true;
            if (this.taskBatchComponent?.instance) {
                this.taskBatchComponent.instance.close();
            }
            this.taskBatchComponent = this.lazyLoadService.appendComponent(
                TaskBatchComponent,
                {
                    taskData: { id: taskId },
                    taskName,
                    customTip,
                    customTipType,
                    onClose: () => {
                        this.windowStatus = false;
                        this.taskBatchComponent = null;
                    },
                },
            );
        }

        this.eventService.emit('updateTaskCount');
        this.addTask(rest, taskName, objName, taskId);
    }

    /**
     * 任务创建失败
     * @param taskName 任务名称
     * @param taskName 任务名称
     * @param param1 返回结果
     */
    private createFailed(taskName: string, objName: string, { result }: HttpResponse, showResult?: boolean): void {
        if (!showResult) {
            return;
        }

        //  超时用单独的标题
        const title = result.code === 1077949006 ? 'common.createTask_timeout_tip' : 'common.createTask_failed_tip';
        let msg = `
            <div class="word-break">${this.i18nService.get(title)}</div>
            <div>
                <span>${this.i18nService.get('common.name_column', true)}</span>
                <span class="word-break">${this.taskNameText[taskName]}</span>
            </div>
        `;
        if (objName) {
            msg += `<div>
                        <span>${this.i18nService.get('common.object_label', true)}</span>
                        <span class="word-break text-wrap">${this.utilService.encodeHTML(objName)}</span>
                    </div>`;
        }
        msg += `<div>
                    <span>${this.i18nService.get('common.description_column', true)}</span>
                    <span class="word-break text-wrap">${this.utilService.encodeHTML(result.description)}</span>
                </div>`;
        if (result.suggestion) {
            msg += `<div>
                        <span>${this.i18nService.get('common.suggest_label', true)}</span>
                        <span class="word-break text-wrap">${this.utilService.encodeHTML(result.suggestion)}</span>
                    </div>`;
        }

        this.toastService.error(msg);
    }

    /**
     * 轮询任务
     */
    private pollTask(): void {
        const rest = this.httpService.makeUrl('task.batchGet', 'V2');

        this.taskPolling$ = this.pollingService.add(rest, 5);
        this.taskPolling$.onPolling = (): any => {
            const tasks = this.getTasks();
            const taskIds = Object.keys(tasks);

            if (!taskIds.length) {
                return false;
            }

            const groupFilters: Filter[][] = taskIds.map((id) => [{ field: 'id', matchMode: '', value: id }]);
            rest.setGropFilters(groupFilters).setRange({ offset: 0, limit: taskIds.length });
            const options: any = { taskIds };
            this.taskPolling$.updateRest([{ rest, method: 'GET', options }]);

            return true;
        };

        this.taskPolling$.subscribe((res) => {
            const taskIds = (res.options as any).taskIds;
            const omtasks: Omtask[] = res.data || [];
            const tasks = this.getTasks();

            for (const taskId of taskIds) {
                const task = tasks[taskId];
                const omtask = omtasks.find((a) => a.id === taskId);

                // 任务不存在，删除本地任务
                if (!omtask) {
                    delete tasks[taskId];
                    continue;
                }

                const isFailOrRollback = omtask.taskStatus === 'fail' ||
                    omtask.taskStatus === 'rollback_fail' ||
                    omtask.taskStatus === 'rollback_success' ||
                    omtask.taskStatus === 'partial_fail';

                if (omtask.taskStatus === 'success') {
                    delete tasks[taskId];
                    this.excuteSuccess(task, omtask);
                } else if (isFailOrRollback) {
                    delete tasks[taskId];
                    this.excuteFailed(task, omtask);
                } else {
                    // 查询过一次之后都标记为需要tips提示
                    task.needTips = true;
                }
                this.taskExcuting$.next({ task, data: omtask });
            }

            this.saveTasks(tasks);
            this.isFirstLoadTask = false;
        });
    }

    /**
     * 任务执行成功
     * @param task 任务ID
     * @param omtask 任务数据
     */
    private excuteSuccess(task: Task, omtask: Omtask): void {
        // 前台执行框存在就不提示，否则提示
        task.needTips = this.taskBatchComponent ? false : task.needTips;

        if (task.needTips) {
            const component = this.lazyLoadService.createComponent(TaskResultComponent, {
                msgType: 'success',
                taskId: omtask.id,
                taskName: this.taskNameText[task.taskName],
                objName: task.objName,
                onClose: () => {
                    this.toastService.close(msg);
                    msg = null;
                },
            });
            let msg = this.toastService.success(component);
        }

        this.taskExcuted$.next({ success: true, task, data: omtask });
        this.eventService.emit('updateTaskCount');
    }

    /**
     * 任务执行失败
     * @param task 任务
     * @param omtask 任务数据
     */
    private excuteFailed(task: Task, omtask: Omtask): void {
        // 前台执行框存在就不提示，否则提示
        task.needTips = this.taskBatchComponent ? false : task.needTips;

        if (task.needTips) {
            const component = this.lazyLoadService.createComponent(TaskResultComponent, {
                msgType: 'error',
                taskId: omtask.id,
                taskName: this.taskNameText[task.taskName],
                objName: task.objName,
                onClose: () => {
                    this.toastService.close(msg);
                    msg = null;
                },
            });
            let msg = this.toastService.error(component);
        }

        this.taskExcuted$.next({ success: false, task, data: omtask });
        this.eventService.emit('updateTaskCount');
    }
}
